import React, { useId } from 'react';
type GlyphProps = {
  className?: string;
};

// Shared fine-line drawing props, so custom glyphs sit beside the line icons
const line = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.25,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const
};

/* ——— Options row ——— */

export function GuestsGlyph({
  className = 'h-[18px] w-[18px]'
}: GlyphProps) {
  // Two champagne coupes meeting in a toast
  return <svg viewBox="0 0 24 24" className={className} {...line} strokeWidth={1.5} aria-hidden="true" data-id="element-967" data-mp="2iavvlsa7cp7o">
      <g transform="rotate(-14 7 12)" data-id="element-968" data-mp="2mcqsmkl3ogfn">
        <path d="M3 6.5H11C11 9.2 9.2 10.8 7 10.8S3 9.2 3 6.5Z" data-id="element-969" data-mp="2r97sud5kql93" />
        <path d="M7 10.8V18M4.6 18H9.4" data-id="element-970" data-mp="2rbtw40lfmeli" />
      </g>
      <g transform="rotate(14 17 12)" data-id="element-971" data-mp="2w9vhis6byq05">
        <path d="M13 6.5H21C21 9.2 19.2 10.8 17 10.8S13 9.2 13 6.5Z" data-id="element-972" data-mp="2w6r557iponye" />
        <path d="M17 10.8V18M14.6 18H19.4" data-id="element-973" data-mp="6iqgukvmkto0" />
      </g>
      <path d="M12 1.6V3.4M9.8 2.4L10.6 3.8M14.2 2.4L13.4 3.8" data-id="element-974" data-mp="bimpwyj6jbaf" />
    </svg>;
}
export function ArrivalGlyph({
  className = 'h-[18px] w-[18px]'
}: GlyphProps) {
  // Compass rose — fine ring, long north–south points
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" data-id="element-975" data-mp="lkxx0jzzex38">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1" data-id="element-976" data-mp="gluq0u0fygsm" />
      <path d="M12 1.5L13.6 10.4L12 12L10.4 10.4Z" fill="currentColor" data-id="element-977" data-mp="gnxnfz3xbc7d" />
      <path d="M12 22.5L13.6 13.6L12 12L10.4 13.6Z" fill="none" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" data-id="element-978" data-mp="bmrwa5pwvwzy" />
      <path d="M4.5 12L10.6 10.9L12 12L10.6 13.1Z" fill="none" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" data-id="element-979" data-mp="1x8cmxudjz41" />
      <path d="M19.5 12L13.4 10.9L12 12L13.4 13.1Z" fill="currentColor" data-id="element-980" data-mp="3u0hiuxybpd89" />
    </svg>;
}

/* ——— Concierge tiles — same 64×30 solid style as the vehicles ——— */

// A silver cloche on its plate, light catching the dome
export function ClocheGlyph({
  className = ''
}: GlyphProps) {
  const maskId = useId().replace(/:/g, '');
  return <svg viewBox="0 0 64 30" className={className} fill="none" aria-hidden="true" data-id="element-981" data-mp="3f4a267m4itxq">
      <defs data-id="element-982" data-mp="35fcvr34968x4">
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="64" height="30" data-id="element-983" data-mp="35ejntytk5dde">
          <rect width="64" height="30" fill="white" data-id="element-984" data-mp="3ag0ktj8x8fop" />
          <path d="M25.6 19.4C26 16.6 27.8 14.7 30.2 13.9" stroke="black" strokeWidth="1.1" strokeLinecap="round" data-id="element-985" data-mp="2wfpsebryqt1f" />
        </mask>
      </defs>
      <g fill="currentColor" data-id="element-986" data-mp="30ht8jdpkult1">
        <path mask={`url(#${maskId})`} d="M20.6 22.4C20.6 15.6 25.6 11 32 11S43.4 15.6 43.4 22.4Z" data-id="element-987" data-mp="30j4bxcf41rjm" />
        <circle cx="32" cy="9.4" r="1.6" data-id="element-988" data-mp="3o8p3ukt8rz51" />
        <rect x="17" y="23.2" width="30" height="1.7" rx="0.85" data-id="element-989" data-mp="3t5yrpw0zg2qr" />
      </g>
      {/* A glint above */}
      <path d="M45.6 6.4V10M43.8 8.2H47.4" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" data-id="element-990" data-mp="3ff3qn5wq6pxq" />
    </svg>;
}

// A solid crest with an inset rim and keyhole — protection, kept private
export function CrestGlyph({
  className = ''
}: GlyphProps) {
  const maskId = useId().replace(/:/g, '');
  return <svg viewBox="0 0 64 30" className={className} fill="none" aria-hidden="true" data-id="element-991" data-mp="frm0ye1f73x6">
      <defs data-id="element-992" data-mp="20gfz3un0ucl5">
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="64" height="30" data-id="element-993" data-mp="20fws38ywt8lw">
          <rect width="64" height="30" fill="white" data-id="element-994" data-mp="1vh3ezhnjbjm9" />
          <path d="M32 7.6L39.6 10.2V15.4C39.6 19.6 36.8 22.6 32 24.2C27.2 22.6 24.4 19.6 24.4 15.4V10.2Z" stroke="black" strokeWidth="0.8" strokeLinejoin="round" data-id="element-995" data-mp="1vgacisvlzg9x" />
          <circle cx="32" cy="14.2" r="1.8" fill="black" data-id="element-996" data-mp="1hhtwucfrqffn" />
          <path d="M31.2 15.2L30.6 19.4H33.4L32.8 15.2Z" fill="black" data-id="element-997" data-mp="1hkg56eahgyyx" />
        </mask>
      </defs>
      <path mask={`url(#${maskId})`} fill="currentColor" d="M32 4.8L42 8.2V15C42 20.8 38 24.6 32 26.6C26 24.6 22 20.8 22 15V8.2Z" data-id="element-998" data-mp="1cldanns4qb57" />
    </svg>;
}

// The concierge: jacket, open collar and bow tie, beside a service bell
export function ConciergeGlyph({
  className = ''
}: GlyphProps) {
  const maskId = useId().replace(/:/g, '');
  return <svg viewBox="0 0 64 30" className={className} fill="none" aria-hidden="true" data-id="element-999" data-mp="2xvtwdmapc32d">
      <defs data-id="element-1000" data-mp="17g9nf3ox2bi8">
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="64" height="30" data-id="element-1001" data-mp="17gt8gt9v5g0h">
          <rect width="64" height="30" fill="white" data-id="element-1002" data-mp="1cfmanpjp9klc" />
          {/* Shirt front */}
          <path d="M26.6 16.2L29 23.4L31.4 16.2Z" fill="black" data-id="element-1003" data-mp="1gfwtw3wi67i5" />
          {/* Lapel lines */}
          <path d="M25.6 16.6L24.6 21.2L27.4 21.8M32.4 16.6L33.4 21.2L30.6 21.8" stroke="black" strokeWidth="0.6" strokeLinejoin="round" data-id="element-1004" data-mp="1lfsojcjb8ml1" />
        </mask>
      </defs>
      <g fill="currentColor" data-id="element-1005" data-mp="1qcjcsn59qohj">
        <circle cx="29" cy="8.8" r="3.7" data-id="element-1006" data-mp="1vcx9j91f6c09" />
        <path mask={`url(#${maskId})`} d="M19.4 26C19.4 19.2 23.4 15.6 29 15.6S38.6 19.2 38.6 26Z" data-id="element-1007" data-mp="1vbvw4xwxldfd" />
        {/* Bow tie */}
        <path d="M27.1 17.4L29 18.3L30.9 17.4V19.6L29 18.7L27.1 19.6Z" data-id="element-1008" data-mp="206jfkid2vhn3" />
        {/* Service bell */}
        <path d="M41.4 24.4C41.4 21 43.6 19 46.4 19S51.4 21 51.4 24.4Z" data-id="element-1009" data-mp="247lf70awpvpl" />
        <rect x="40" y="25" width="12.8" height="1.2" rx="0.6" data-id="element-1010" data-mp="296omysi9ug19" />
        <circle cx="46.4" cy="17.8" r="0.9" data-id="element-1011" data-mp="2e356f9ej0680" />
      </g>
    </svg>;
}

/* ——— Destination → concierge divider ——— */

// Bugatti Chiron in profile, nose to the right (towards the next field).
// Solid like the jet, with the car's defining cues cut in: very low teardrop
// glasshouse, the chrome C-line sweeping from the roof down and forward under the
// door, two-tone body split at that line, short pointed nose with horseshoe hint,
// fastback into a tall cut-off tail with the LED bar, multi-spoke wheels.
export function HypercarGlyph({
  className = ''
}: GlyphProps) {
  const ids = useId().replace(/:/g, '');
  const maskId = `${ids}m`;
  const clipId = `${ids}c`;
  const body = 'M5.2 21.4L4.7 15.6C4.7 14.8 5.1 14.3 5.9 14.2L13.4 13.3C18 11.2 23.4 9.5 29.2 9.2C33.8 9 37.6 10 40.4 11.6L51.8 14.3C56.4 15.2 59.8 16.2 61.1 17.6C61.6 18.2 61.4 19.8 60.8 20.8C60.6 21.2 60.2 21.4 59.8 21.4H54.2A5.1 5.1 0 0 0 44 21.4H19.2A5.1 5.1 0 0 0 9 21.4Z';
  const cLine = 'M34.2 13.2C27.4 13.2 23 15.4 22.6 18.8C22.5 19.9 22.9 20.8 23.6 21.5';
  return <svg viewBox="0 0 64 30" className={className} fill="none" aria-hidden="true" data-id="element-1012" data-mp="3kwb4ju7johyb">
      <defs data-id="element-1013" data-mp="1j1dmuhnbbexu">
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="64" height="30" data-id="element-1014" data-mp="1j2yadnb7lxm7">
          <rect width="64" height="30" fill="white" data-id="element-1015" data-mp="1o1rlxa6xrejq" />
          {/* Teardrop glasshouse with B-pillar */}
          <path d="M20.6 12.8C24.4 10.9 28.4 10.2 31.8 10.3C35 10.4 37.6 11.3 39.2 12.4L22.2 13.4Z" fill="black" data-id="element-1016" data-mp="198y7ls3eh4gn" />
          <path d="M31.6 10.3L31.2 13" stroke="white" strokeWidth="0.7" data-id="element-1017" data-mp="10a46ppj1drxa" />
          {/* Chrome C-line */}
          <path d={cLine} stroke="black" strokeWidth="1.25" strokeLinecap="round" data-id="element-1018" data-mp="158xklj28jkxz" />
          {/* Side intake inside the C */}
          <path d="M30.6 14.6C27.2 14.9 25.2 16.3 24.9 18.4L26.6 18.6C27 17 28.4 15.9 30.8 15.5Z" fill="black" data-id="element-1019" data-mp="qczscbvfn0r1" />
          {/* Headlight, horseshoe hint, tail LED bar */}
          <path d="M51.6 14.9L57 16.1" stroke="black" strokeWidth="0.8" strokeLinecap="round" data-id="element-1020" data-mp="2kq7fdhd62824" />
          <path d="M60.3 17.9C59.4 18.3 59.1 19.4 59.6 20.5" stroke="black" strokeWidth="0.6" strokeLinecap="round" data-id="element-1021" data-mp="2posgdj08svrm" />
          <path d="M5.4 16.4H8.6" stroke="black" strokeWidth="0.8" strokeLinecap="round" data-id="element-1022" data-mp="e0xd1rfq1nob" />
          {/* Sill blade */}
          <path d="M24.6 20.2H43.4" stroke="black" strokeWidth="0.45" strokeLinecap="round" data-id="element-1023" data-mp="93x5fc384cv3" />
        </mask>
        {/* Everything behind the C-line — the second body tone */}
        <clipPath id={clipId} data-id="element-1024" data-mp="j0a1uhq5l4tj">
          <path d="M0 0H34.2V13.2C27.4 13.2 23 15.4 22.6 18.8C22.5 19.9 22.9 20.8 23.6 21.5V30H0Z" data-id="element-1025" data-mp="iz79e5d6mubi" />
        </clipPath>
      </defs>
      {/* Speed lines */}
      <path d="M0.4 15.8H2.6M0 18.6H2" stroke="currentColor" strokeWidth="0.7" strokeLinecap="round" strokeDasharray="0.3 1.6" data-id="element-1026" data-mp="sr4ldec0lqba" />
      <path d={body} fill="currentColor" mask={`url(#${maskId})`} data-id="element-1027" data-mp="16p1kahdxy8t4" />
      {/* Two-tone: rear section a shade lighter */}
      <path d={body} fill="white" opacity="0.32" clipPath={`url(#${clipId})`} mask={`url(#${maskId})`} data-id="element-1028" data-mp="11s2qy123q5vz" />
      {/* Wheels: tyre, fine rim, five spokes, hub */}
      {[14.1, 49.1].map(cx => <g key={cx} data-id="element-1029" data-mp="33k7ivcjlp36p">
          <circle cx={cx} cy="21.4" r="4.1" fill="currentColor" data-id="element-1030" data-mp="33kzbcehwiksr" />
          <circle cx={cx} cy="21.4" r="2.8" stroke="white" strokeWidth="0.45" data-id="element-1031" data-mp="2hop943syryk6" />
          {[0, 72, 144, 216, 288].map(deg => <path key={deg} d={`M${cx} 21.4V18.8`} stroke="white" strokeWidth="0.4" transform={`rotate(${deg} ${cx} 21.4)`} data-id="element-1032" data-mp="2mm7c7dkavjk9" />)}
          <circle cx={cx} cy="21.4" r="0.6" fill="white" data-id="element-1033" data-mp="2wemts9roq64m" />
        </g>)}
      {/* Ground shadow */}
      <path d="M7 26.2H58" stroke="currentColor" strokeWidth="0.6" strokeLinecap="round" opacity="0.2" data-id="element-1034" data-mp="1yreg30xxe1d6" />
    </svg>;
}

// Lamborghini Huracán in profile, nose to the right (towards the next field).
// Pure wedge: knife-edge nose, one long windscreen line up to a roof peak set well
// back, short sloping engine cover with louvres, cut-off tail. Angular hexagonal
// arches, the big triangular intake behind the door, sharp trapezoid side glass,
// Y-slit headlight and Y-spoke wheels — solid like the jet, details cut in.
export function HuracanGlyph({
  className = ''
}: GlyphProps) {
  const maskId = useId().replace(/:/g, '');
  return <svg viewBox="0 0 64 30" className={className} fill="none" aria-hidden="true" data-id="element-1035" data-mp="1dhrw0xl589v3">
      <defs data-id="element-1036" data-mp="18h47cike0541">
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="64" height="30" data-id="element-1037" data-mp="18ip1w86pbo1w">
          <rect width="64" height="30" fill="white" data-id="element-1038" data-mp="13o1cm73vipm9" />
          {/* Trapezoid side glass, pointed forward */}
          <path d="M23.2 13.4L28 11.1L33.6 10.9L41.2 13.1L24.4 14Z" fill="black" data-id="element-1039" data-mp="yooo7hxctahk" />
          {/* Triangular intake behind the door */}
          <path d="M20.2 16L27.4 14.8L24.6 18.8L21 19Z" fill="black" data-id="element-1040" data-mp="ppl5wg60gz44" />
          {/* Door blade rising from the front arch into the intake */}
          <path d="M43.6 17.8L28.4 15.6" stroke="black" strokeWidth="0.5" strokeLinecap="round" data-id="element-1041" data-mp="krjm1t0rgvrl" />
          {/* Y-slit headlight */}
          <path d="M51.4 15.3L57.8 16.8M54.6 16.1L56.2 17.4" stroke="black" strokeWidth="0.7" strokeLinecap="round" data-id="element-1042" data-mp="fu9mhkjnr3q1" />
          {/* Front splitter intake */}
          <path d="M57.8 19.2L61 18.6" stroke="black" strokeWidth="0.6" strokeLinecap="round" data-id="element-1043" data-mp="2a79aa0y6z48e" />
          {/* Engine cover louvres */}
          {[10.4, 12.6, 14.8].map(x => <path key={x} d={`M${x} 15.4L${x + 1.4} 14.4`} stroke="black" strokeWidth="0.5" strokeLinecap="round" data-id="element-1044" data-mp="2wx6h788e1d87" />)}
          {/* Tail light slit and diffuser fins */}
          <path d="M5.8 16.4H9.6" stroke="black" strokeWidth="0.7" strokeLinecap="round" data-id="element-1045" data-mp="36uucbz9womqw" />
          <path d="M6.6 20.8L7.4 19.4M8.2 20.9L8.9 19.6" stroke="black" strokeWidth="0.45" strokeLinecap="round" data-id="element-1046" data-mp="2j4qj8lucg5e5" />
        </mask>
      </defs>
      {/* Speed lines */}
      <path d="M0.4 15.6H2.8M0 18.6H2.2" stroke="currentColor" strokeWidth="0.7" strokeLinecap="round" strokeDasharray="0.3 1.6" data-id="element-1047" data-mp="2e7q8vyqgogh8" />
      {/* Wedge body with angular hexagonal arches */}
      <path fill="currentColor" mask={`url(#${maskId})`} d="M6.2 21.4L5.4 17.6L5.8 15.6L8.2 14.8L16 13.4L27 10.2L31.4 9.6L35 10L44 12.6L56 15.8L60.4 17L62.6 18L61.4 19.6L58.8 20.5L56.4 21.4H54.6L53.6 17.8L50.6 16.4H47.4L44.6 17.8L43.8 21.4H19.8L18.6 17.6L15.8 16.4H12.8L10 17.8L9 21.4Z" data-id="element-1048" data-mp="2n50q00pymzu2" />
      {/* Y-spoke wheels */}
      {[14.3, 49.1].map(cx => <g key={cx} data-id="element-1049" data-mp="1q2z62desm4xj">
          <circle cx={cx} cy="21.4" r="4" fill="currentColor" data-id="element-1050" data-mp="1q3idv1aqbcxu" />
          <circle cx={cx} cy="21.4" r="2.8" stroke="white" strokeWidth="0.45" data-id="element-1051" data-mp="1be2qlvaeppmw" />
          {[0, 72, 144, 216, 288].map(deg => <path key={deg} d={`M${cx} 21.4V19.6M${cx} 19.6L${cx - 0.7} 18.8M${cx} 19.6L${cx + 0.7} 18.8`} stroke="white" strokeWidth="0.35" strokeLinecap="round" transform={`rotate(${deg} ${cx} 21.4)`} data-id="element-1052" data-mp="1gcnpnrdxa39r" />)}
          <circle cx={cx} cy="21.4" r="0.55" fill="white" data-id="element-1053" data-mp="1u5vg3fq9ospv" />
        </g>)}
      {/* Ground shadow */}
      <path d="M7 26.2H58" stroke="currentColor" strokeWidth="0.6" strokeLinecap="round" opacity="0.2" data-id="element-1054" data-mp="1z4ovxet10vt7" />
    </svg>;
}

// Motor speedboat in profile, bow to the right, planing at speed: the bow lifts
// clear of the water, twin outboard engines hang off the transom, a low console
// windscreen sits amidships, and a rooster-tail spray kicks up behind the props
export function SpeedboatGlyph({
  className = ''
}: GlyphProps) {
  const maskId = useId().replace(/:/g, '');
  const engine = 'M0 0H4.6C5.3 0 5.8 0.5 5.8 1.2V5.4H1C0.4 5.4 0 5 0 4.4Z';
  return <svg viewBox="0 0 64 30" className={className} fill="none" aria-hidden="true" data-id="element-1055" data-mp="2bkom5xl5vu4q">
      <defs data-id="element-1056" data-mp="26ng0vo4pv9z9">
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="64" height="30" data-id="element-1057" data-mp="26n685ssaeopv">
          <rect width="64" height="30" fill="white" data-id="element-1058" data-mp="22jqcayo1kse8" />
          {/* Sheer stripe */}
          <path d="M13 18.6L58.4 16.2" stroke="black" strokeWidth="0.55" strokeLinecap="round" data-id="element-1059" data-mp="1xkx0daoel9g6" />
          {/* Windscreen glass */}
          <path d="M35.4 15.5L38.6 13.2H40.6L40.2 15.4Z" fill="black" data-id="element-1060" data-mp="1so6mmu02mpxx" />
          {/* Cowling stripes on the outboards */}
          <path d="M6.2 12.9H10.8M3.8 13.5H8.4" stroke="black" strokeWidth="0.5" strokeLinecap="round" data-id="element-1061" data-mp="3rxn2ugsu7qz6" />
        </mask>
      </defs>

      {/* Rooster-tail spray behind the props */}
      <path d="M5.6 22.2C3.4 21 1.8 19.2 0.8 16.8" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" strokeDasharray="0.3 1.8" data-id="element-1062" data-mp="3ujvxzbucbgm1" />
      <path d="M6.4 23C3.8 23.2 1.8 22.8 0 22" stroke="currentColor" strokeWidth="0.7" strokeLinecap="round" strokeDasharray="0.3 2" opacity="0.7" data-id="element-1063" data-mp="84us0vtnebdz" />
      {/* Waterline and bow wave */}
      <path d="M9 24.2H56" stroke="currentColor" strokeWidth="0.6" strokeLinecap="round" opacity="0.22" data-id="element-1064" data-mp="3blb9lrju2pc3" />
      <path d="M40 22.8C45 22.8 49 22.2 52 21" stroke="currentColor" strokeWidth="0.6" strokeLinecap="round" opacity="0.45" data-id="element-1065" data-mp="3gkecp6g96lhf" />

      {/* Boat, nose lifted as it planes */}
      <g transform="rotate(-5 11 21)" fill="currentColor" mask={`url(#${maskId})`} data-id="element-1066" data-mp="3llkahiorsyxt">
        {/* Second outboard, set behind */}
        <g transform="translate(3.2 11.8)" opacity="0.5" data-id="element-1067" data-mp="10tigt1dlviwa">
          <path d={engine} data-id="element-1068" data-mp="10syxphc7yogo" />
          <path d="M2.4 5.4V10.2M1.4 10.2H3.8" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" fill="none" data-id="element-1069" data-mp="15sbjdyrctpho" />
        </g>
        {/* Lead outboard */}
        <g transform="translate(5.6 11.2)" data-id="element-1070" data-mp="13e6n8nfh6jt7">
          <path d={engine} data-id="element-1071" data-mp="zccqgq8ehzyb" />
          <path d="M2.4 5.4V10.6M1.4 10.6H3.8" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" fill="none" data-id="element-1072" data-mp="zd4os8tdubsa" />
        </g>
        {/* Deep-V hull with a sharp, raked bow */}
        <path d="M11 16.6L52 15.2C56.6 15 60 14.4 62.6 13.4C61 16.6 57.6 18.8 52 19.6L13.4 20.6C11.8 20.6 11 19.4 11 16.6Z" data-id="element-1073" data-mp="18c8555160d33" />
        {/* Console windscreen */}
        <path d="M33.4 15.8L38 12.4H41.8L41.2 15.6Z" data-id="element-1074" data-mp="1ma68lfdy6p3t" />
        {/* Helm seats */}
        <path d="M24.6 16.2C24.6 14.8 25.4 14.2 26.6 14.2S28.6 14.8 28.6 16.1Z" data-id="element-1075" data-mp="1iap34lyitryn" />
        <path d="M29.4 16.1C29.4 14.8 30.1 14.3 31.2 14.3S33 14.8 33 16Z" data-id="element-1076" data-mp="1w10xysmhq4th" />
      </g>
    </svg>;
}

// Gulfstream-style long-range jet in profile, climbing: long nose, oval windows,
// T-tail and rear-mounted engines, with a fine dotted contrail behind
export function GulfstreamGlyph({
  className = ''
}: GlyphProps) {
  const maskId = useId().replace(/:/g, '');
  return <svg viewBox="0 0 64 30" className={className} fill="none" aria-hidden="true" data-id="element-1077" data-mp="2kmyxa3yagqu5">
      <defs data-id="element-1078" data-mp="2ook7fhjfur0n">
        <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="64" height="30" data-id="element-1079" data-mp="2op3okw19lbdm">
          <rect width="64" height="30" fill="white" data-id="element-1080" data-mp="2apkh85ga242d" />
          {/* Signature oval windows */}
          {[27, 31, 35, 39, 43, 47].map(x => <ellipse key={x} cx={x} cy="11.3" rx="1.05" ry="0.75" fill="black" data-id="element-1081" data-mp="2fmauym4m0nkm" />)}
          {/* Cockpit glazing */}
          <path d="M55.4 10.2L58.4 10.6L57.2 11.4L55 11.3Z" fill="black" data-id="element-1082" data-mp="25py21qgw2dqx" />
        </mask>
      </defs>
      {/* Contrail */}
      <path d="M1 28 C7 27.4 12 25.8 17 23.2" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" strokeDasharray="0.4 2.4" data-id="element-1083" data-mp="1rz30kz8pyyxd" />
      <g transform="rotate(-12 34 13)" fill="currentColor" data-id="element-1084" data-mp="1wy5v3pr2pmr3">
        <g mask={`url(#${maskId})`} data-id="element-1085" data-mp="1hzve9mk0bty4">
          {/* Fuselage — long tapered nose, slim tail cone */}
          <path d="M62.5 12.6C61.6 10.9 58.9 9.6 54.5 9.4L19 9.2L11 9.7L5.5 11.2L5.5 12.4L11 13.5L19 14.1L54.5 14.4C58.9 14.4 61.6 13.8 62.5 12.6Z" data-id="element-1086" data-mp="1mzzudbh06mnt" />
        </g>
        {/* Swept fin */}
        <path d="M15.5 9.5L9.4 2L6.6 2L8.6 10Z" data-id="element-1087" data-mp="z4en23ab7jkr" />
        {/* T-tail */}
        <path d="M4.6 1.6H12.2L11.6 2.7H5.2Z" data-id="element-1088" data-mp="z2201bpp2j0u" />
        {/* Rear engine with pylon */}
        <rect x="13.6" y="6.4" width="8.6" height="2.5" rx="1.25" data-id="element-1089" data-mp="u5bh8gmi99bq" />
        <path d="M17 8.9H19.4L19.6 9.3H16.8Z" data-id="element-1090" data-mp="p70704saswsz" />
        {/* Wing, swept back, with raised winglet */}
        <path d="M38.5 14.2L24.6 18.8L21.8 18.8L30.4 14.2Z" data-id="element-1091" data-mp="1mso28md0p92x" />
        <path d="M22.2 18.8L20.6 16.6L21.4 16.6L23.4 18.8Z" data-id="element-1092" data-mp="1huw1r3yb4en4" />
      </g>
    </svg>;
}